import { getClientsMinimal, IClientMinimal } from "@/api";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { ChevronDownIcon, XIcon } from "lucide-react";
import { useId, useMemo } from "react";
import { Button } from "../ui/button";
import {
    Combobox,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
    ComboboxSeparator,
    ComboboxTrigger,
    ComboboxValue,
} from "../ui/combobox";
import { Label } from "../ui/label";

/**
 * Shadcn/Base UI–backed client picker. Drop-in shape parity with the
 * legacy MUI `ClientAutocomplete` so a future swap inside other dialogs
 * is a one-line import change. Shares the `['clients-minimal']` query
 * key + 5 min staleTime with `ClientAutocomplete`, so the two pickers
 * hydrate each other's cache.
 */
interface ClientComboboxProps {
    value: IClientMinimal | null;
    onChange: (client: IClientMinimal | null) => void;
    label?: string;
    placeholder?: string;
    /**
     * When set, pins an "all clients" option with this label at the top
     * of the popup, divided from the client list (the OrganizationCombobox
     * pattern). Selecting it clears the value, and it shows as selected
     * while `value` is null.
     */
    allOptionLabel?: string;
    required?: boolean;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
    className?: string;
    id?: string;
}

/** Sentinel id for the pinned all-clients option; never a real client id. */
const ALL_CLIENTS_ID = -1;

export const ClientCombobox = ({
    value,
    onChange,
    label,
    placeholder = "Tag a client (optional)",
    allOptionLabel,
    required = false,
    disabled = false,
    error = false,
    helperText,
    className,
    id,
}: ClientComboboxProps) => {
    const generatedId = useId();
    const triggerId = id ?? generatedId;

    const { data: clients, isPending } = useQuery({
        queryKey: ["clients-minimal"],
        queryFn: getClientsMinimal,
        staleTime: 5 * 60 * 1000,
    });

    const clientOptions = useMemo(() => clients ?? [], [clients]);

    const selectedClient = useMemo(
        () => clientOptions.find((c) => c.id === value?.id) ?? null,
        [value?.id, clientOptions]
    );

    // A real ComboboxItem (not the OrganizationCombobox's plain button,
    // which leaves the popup open on click) so selection closes the
    // popup and the check indicator renders for free. Stable identity:
    // Base UI matches the root value to this item by reference.
    const allOption = useMemo<IClientMinimal | null>(
        () =>
            allOptionLabel
                ? { id: ALL_CLIENTS_ID, name: allOptionLabel }
                : null,
        [allOptionLabel]
    );

    const handleClear = () => {
        onChange(null);
    };

    const showClear = value !== null && !disabled;

    return (
        <div className={cn("flex flex-col gap-1.5", className)}>
            {label && (
                <Label htmlFor={triggerId}>
                    {label}
                    {required && (
                        <span className="text-destructive ml-0.5">*</span>
                    )}
                </Label>
            )}
            <Combobox
                items={clientOptions}
                itemToStringLabel={(client: IClientMinimal) => client.name}
                value={value === null ? allOption : selectedClient}
                onValueChange={(c: IClientMinimal | null) =>
                    onChange(c === null || c.id === ALL_CLIENTS_ID ? null : c)
                }
            >
                {/* Trigger content lives inside the Button render prop so
                    Base UI's render-prop override suppresses the
                    primitive's auto-chevron. Splitting via ComboboxTrigger
                    children would leak a second chevron from the
                    `ui/combobox.tsx` primitive. Matches OrganizationCombobox. */}
                <ComboboxTrigger
                    render={
                        <Button
                            id={triggerId}
                            variant="outline"
                            className={cn(
                                "w-full justify-between font-normal",
                                error &&
                                    "border-destructive focus-visible:ring-destructive/30"
                            )}
                            disabled={disabled}
                        >
                            <span className="min-w-0 flex-1 truncate text-left">
                                <ComboboxValue placeholder={placeholder} />
                            </span>
                            {/* One icon slot: chevron while empty, clear-X
                                once a value is set. Both render at a 16px
                                footprint (size-4 vs p-0.5 + size-3) so the
                                swap doesn't shift the trigger layout. */}
                            <span className="flex shrink-0 items-center">
                                {showClear ? (
                                    <span
                                        role="button"
                                        aria-label="Clear client"
                                        className="text-muted-foreground hover:text-foreground rounded-sm p-0.5"
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleClear();
                                        }}
                                    >
                                        <XIcon className="size-3" />
                                    </span>
                                ) : (
                                    <ChevronDownIcon className="text-muted-foreground pointer-events-none size-4" />
                                )}
                            </span>
                        </Button>
                    }
                />
                <ComboboxContent>
                    <ComboboxInput
                        showTrigger={false}
                        placeholder="Search clients"
                    />
                    <ComboboxEmpty>
                        {isPending ? "Loading…" : "No clients found"}
                    </ComboboxEmpty>
                    {/* Pinned above ComboboxList so it sits outside the
                        scroll container (always visible with long client
                        lists) and outside the search filter. px-1/pt-1
                        match the list's p-1 inset. */}
                    {allOption && (
                        <>
                            <div className="px-1 pt-1">
                                <ComboboxItem
                                    value={allOption}
                                    className="cursor-pointer"
                                >
                                    {allOption.name}
                                </ComboboxItem>
                            </div>
                            <ComboboxSeparator />
                        </>
                    )}
                    <ComboboxList>
                        {(client: IClientMinimal) => (
                            <ComboboxItem
                                key={client.id}
                                value={client}
                                className="cursor-pointer"
                            >
                                {client.name}
                            </ComboboxItem>
                        )}
                    </ComboboxList>
                </ComboboxContent>
            </Combobox>
            {helperText && (
                <p
                    className={cn(
                        "text-xs",
                        error ? "text-destructive" : "text-muted-foreground"
                    )}
                >
                    {helperText}
                </p>
            )}
        </div>
    );
};
